import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import { FaEye, FaEyeSlash } from 'react-icons/fa'; // <-- 1. IMPORT THE ICONS
import './BrokerProfile.css'; // Your CSS file

// Helper to get initials for the avatar
const getInitials = (name = '') => {
  if (!name) return 'U';
  const words = name.split(' ');
  return (words[0][0] + (words.length > 1 ? words[words.length - 1][0] : '')).toUpperCase();
};

const ManagerProfile = () => {
  const navigate = useNavigate();

  // --- COMPONENT STATE ---
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    licenseNumber: '',
    agencyName: '',
  });
  const [originalProfile, setOriginalProfile] = useState(null);
  const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });

  // --- 2. NEW STATE FOR PASSWORD VISIBILITY ---
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // UI & Messaging State
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // --- DATA FETCHING ---
  useEffect(() => {
    const userInfo = JSON.parse(localStorage.getItem('user'));
    if (userInfo) {
      const userProfile = {
        name: userInfo.name || '',
        email: userInfo.email || '',
        phone: userInfo.phoneNumber || '',
        role: userInfo.role || 'User',
        licenseNumber: userInfo.licenseNumber || '',
        agencyName: userInfo.agencyName || '',
      };
      setProfile(userProfile);
      setOriginalProfile(userProfile);
    } else {
      navigate('/');
    }
  }, [navigate]);

  // --- NOTIFICATION HANDLER ---
  const showMessage = (setter, message) => {
    setter(message);
    setTimeout(() => setter(''), 4000);
  };

  // --- EVENT HANDLERS ---
  const handleProfileChange = (e) => {
    setProfile({ ...profile, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setOriginalProfile(profile);
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setProfile(originalProfile);
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');
    try {
      const updatePayload = {
        name: profile.name,
        email: profile.email,
        phoneNumber: profile.phone,
        licenseNumber: profile.licenseNumber,
        agencyName: profile.agencyName,
      };
      await authService.updateProfile(updatePayload);
      setIsEditing(false);
      showMessage(setSuccessMessage, 'Profile updated successfully!');
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update profile.';
      showMessage(setErrorMessage, msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    if (passwordData.new !== passwordData.confirm) {
      showMessage(setErrorMessage, "New passwords do not match.");
      return;
    }
    setIsSubmittingPassword(true);
    try {
      await authService.changePassword(passwordData.current, passwordData.new);
      setPasswordData({ current: '', new: '', confirm: '' });
      showMessage(setSuccessMessage, 'Password changed successfully!');
    } catch (err) {
      const msg = err.response?.data?.message || 'Could not change password.';
      showMessage(setErrorMessage, msg);
    } finally {
      setIsSubmittingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      await authService.deleteProfile();
      navigate('/');
    } catch (err) {
      setIsDeleteModalOpen(false);
      const msg = err.response?.data?.message || 'Failed to delete account.';
      showMessage(setErrorMessage, msg);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="manager-profile-container">
      {successMessage && <div className="manager-banner success">{successMessage}</div>}
      {errorMessage && <div className="manager-banner error">{errorMessage}</div>}

      <header className="manager-header">
        <div className="manager-avatar">{getInitials(profile.name)}</div>
        <div className="manager-header-info">
          <h1>{profile.name}</h1>
          <p>{profile.role}</p>
        </div>
      </header>

      <main className="manager-main-content">
        {/* Profile Details Form */}
        <form className="manager-form-card" onSubmit={handleProfileSave}>
          <header className="form-card-header">
            <h3>Account Information</h3>
            {!isEditing && <button type="button" className="btn-edit" onClick={handleEditClick}>Edit</button>}
          </header>
          <div className="form-card-body">
            <div className="form-group"><label>Full Name</label>{isEditing ? <input type="text" name="name" value={profile.name} onChange={handleProfileChange} /> : <p>{profile.name}</p>}</div>
            <div className="form-group"><label>Email Address</label>{isEditing ? <input type="email" name="email" value={profile.email} onChange={handleProfileChange} /> : <p>{profile.email}</p>}</div>
            <div className="form-group"><label>Phone Number</label>{isEditing ? <input type="tel" name="phone" value={profile.phone} onChange={handleProfileChange} /> : <p>{profile.phone || 'N/A'}</p>}</div>
            <div className="form-group"><label>License Number</label>{isEditing ? <input type="text" name="licenseNumber" value={profile.licenseNumber} onChange={handleProfileChange} /> : <p>{profile.licenseNumber || 'N/A'}</p>}</div>
            <div className="form-group"><label>Agency Name</label>{isEditing ? <input type="text" name="agencyName" value={profile.agencyName} onChange={handleProfileChange} /> : <p>{profile.agencyName || 'N/A'}</p>}</div>
          </div>
          {isEditing && (
            <footer className="form-card-footer">
              <button type="button" className="btn-cancel" onClick={handleCancelClick} disabled={isSubmitting}>Cancel</button>
              <button type="submit" className="btn-save" disabled={isSubmitting}>{isSubmitting ? 'Updating...' : 'Update Information'}</button>
            </footer>
          )}
        </form>

        {/* Security Column */}
        <div className="manager-security-column">
          {/* --- 3. UPDATED CHANGE PASSWORD FORM --- */}
          <form className="manager-form-card" onSubmit={handlePasswordSave}>
            <header className="form-card-header"><h3>Change Password</h3></header>
            <div className="form-card-body">

              <div className="form-group">
                <label>Current Password</label>
                <div className="password-input-wrapper">
                  <input type={showCurrentPassword ? "text" : "password"} name="current" value={passwordData.current} onChange={handlePasswordChange} required />
                  <button type="button" className="password-toggle-btn" onClick={() => setShowCurrentPassword(!showCurrentPassword)}>
                    {showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>New Password</label>
                <div className="password-input-wrapper">
                  <input type={showNewPassword ? "text" : "password"} name="new" value={passwordData.new} onChange={handlePasswordChange} required />
                  <button type="button" className="password-toggle-btn" onClick={() => setShowNewPassword(!showNewPassword)}>
                    {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Confirm New Password</label>
                <div className="password-input-wrapper">
                  <input type={showConfirmPassword ? "text" : "password"} name="confirm" value={passwordData.confirm} onChange={handlePasswordChange} required />
                  <button type="button" className="password-toggle-btn" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

            </div>
            <footer className="form-card-footer">
              <button type="submit" className="btn-save" disabled={isSubmittingPassword}>{isSubmittingPassword ? 'Updating...' : 'Update Password'}</button>
            </footer>
          </form>

          {/* Danger Zone */}
          <div className="manager-form-card danger-zone">
            <header className="form-card-header"><h3>Danger Zone</h3></header>
            <div className="form-card-body">
              <p>Deleting your account is permanent and cannot be undone. All your data will be removed.</p>
              <button type="button" className="btn-danger" onClick={() => setIsDeleteModalOpen(true)}>Delete My Account</button>
            </div>
          </div>
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Confirm Account Deletion</h3>
            <p>Are you absolutely sure you want to delete your account? This action is irreversible.</p>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setIsDeleteModalOpen(false)} disabled={isDeleting}>Cancel</button>
              <button className="btn-danger" onClick={handleDeleteAccount} disabled={isDeleting}>{isDeleting ? 'Deleting...' : 'Confirm Delete'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerProfile;